<template>
  <q-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" maximized>
    <q-card class="disc-card">
      <q-bar class="bg-indigo-9 text-white disc-main-bar">
        <q-icon name="undo" size="sm" />
        <div class="disc-main-titles">
          <span v-if="projectName" class="disc-main-project" :title="projectName">
            <q-icon name="apartment" size="11px" class="q-mr-xs" />{{ projectName }}
          </span>
          <span class="disc-main-title">Сторнирование работ</span>
        </div>
        <q-space />
        <div class="row q-gutter-sm items-center q-mr-md" v-if="!data.loading">
          <q-badge color="white" text-color="grey-8" class="q-pa-xs">
            Групп: {{ groupedRows.length }}
          </q-badge>
          <q-badge color="white" text-color="grey-8" class="q-pa-xs">
            Записей: {{ totalFactCount }}
          </q-badge>
          <q-badge color="indigo-2" text-color="indigo-10" class="q-pa-xs" v-if="selectedFactIds.length">
            ✓ Выбрано: {{ selectedFactIds.length }}
          </q-badge>
        </div>
        <q-btn dense flat icon="close" v-close-popup />
      </q-bar>

      <div class="disc-filters q-pa-sm row items-center q-gutter-sm">
        <q-btn
          icon="refresh"
          label="Обновить данные с УСП"
          flat dense no-caps size="sm"
          color="indigo-7"
          :loading="data.loading"
          @click="$emit('refresh')"
        >
          <q-tooltip>Обновить список работ</q-tooltip>
        </q-btn>
        <q-separator vertical class="q-mx-xs" />
        <q-select
          v-model="filterType"
          :options="typeOptions"
          label="Тип проекта"
          dense outlined clearable
          style="min-width:180px"
        />
        <q-select
          v-model="filterBlock"
          :options="blockOptions"
          label="Блок"
          dense outlined clearable
          style="min-width:130px"
        />
        <q-select
          v-model="filterFloor"
          :options="floorOptions"
          label="Этаж"
          dense outlined clearable emit-value map-options
          style="min-width:90px"
        />
        <q-input
          v-model="search"
          label="Поиск"
          dense outlined clearable
          style="min-width:160px"
        >
          <template v-slot:prepend><q-icon name="search" /></template>
        </q-input>
        <q-space />
      </div>

      <div class="disc-tabs-row row items-center q-gutter-sm q-px-sm q-py-xs">
        <q-btn
          flat dense no-caps size="xs"
          icon="unfold_more"
          color="indigo-7"
          label="Развернуть все"
          @click="expandAll"
        />
        <q-btn
          flat dense no-caps size="xs"
          icon="unfold_less"
          color="grey-7"
          label="Свернуть все"
          @click="collapseAll"
        />
        <q-separator vertical class="q-mx-xs" />
        <q-btn
          v-if="selectedFactIds.length"
          dense flat no-caps size="sm"
          icon="undo"
          color="indigo-7"
          :label="`Сторнировать (${selectedFactIds.length} зап. → ${uniquePeriodsCount} операций)`"
          @click="executeStorno"
        >
          <q-tooltip>
            Сторно по выбранным записям: отправим −(fact_smr) и −(fact_mat) каждой записи.<br>
            Записи группируются по (work_guid, период) → {{ uniquePeriodsCount }} операций UpdateFact.<br>
            Сумма к сторно: {{ fmtDisc(selectedSummary.fact_total) }} ₸
          </q-tooltip>
        </q-btn>
        <q-btn
          v-if="selectedFactIds.length"
          dense flat no-caps size="xs"
          icon="clear"
          color="grey-6"
          label="Снять"
          @click="selectedFactMap = {}"
        />
        <q-space />
        <span class="text-caption text-grey-7">
          Показано {{ filteredRows.length }} из {{ groupedRows.length }} групп ({{ filteredFactCount }} из {{ totalFactCount }} записей)
        </span>
      </div>

      <div class="disc-scroll">
        <table class="disc-table">
          <thead>
            <tr>
              <th rowspan="2" class="disc-th-info" style="width:24px"></th>
              <th rowspan="2" class="disc-th-info disc-th-check" style="width:32px"></th>
              <th rowspan="2" class="disc-th-info" style="width:12%">Объект</th>
              <th rowspan="2" class="disc-th-info" style="width:25%">Работа</th>
              <th colspan="2" class="disc-th-smeta-group">СМЕТА (₸)</th>
              <th colspan="2" class="disc-th-operfact">
                ОПЕРФАКТ (₸)
                <q-icon name="help_outline" size="14px" class="q-ml-xs">
                  <q-tooltip>Сравнение текущего факта: Наши данные vs УСП</q-tooltip>
                </q-icon>
              </th>
              <th rowspan="2" class="disc-th-delta" style="width:11%">
                Разница (₸)
                <q-icon name="help_outline" size="14px" class="q-ml-xs">
                  <q-tooltip>Наш ОПЕРФАКТ минус ОПЕРФАКТ УСП</q-tooltip>
                </q-icon>
              </th>
              <th rowspan="2" class="disc-th-delta" style="width:4%">
                %
                <q-icon name="help_outline" size="14px" class="q-ml-xs">
                  <q-tooltip>Процент отклонения: (Разница / ОПЕРФАКТ УСП) * 100</q-tooltip>
                </q-icon>
              </th>
              <th rowspan="2" class="disc-th-info" style="width:8%">Отправлено</th>
            </tr>
            <tr>
              <th class="disc-th-smeta" style="width:9%">Наша</th>
              <th class="disc-th-smeta-usp" style="width:9%">УСП</th>
              <th class="disc-th-ours" style="width:9%">
                Наша
                <q-icon name="help_outline" size="13px" class="q-ml-xs">
                  <q-tooltip>Сумма факта из нашей системы</q-tooltip>
                </q-icon>
              </th>
              <th class="disc-th-usp" style="width:9%">
                УСП
                <q-icon name="help_outline" size="13px" class="q-ml-xs">
                  <q-tooltip>Сумма факта, отправленная/хранящаяся в УСП</q-tooltip>
                </q-icon>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="data.loading">
              <td colspan="11" class="q-pa-lg">
                <div class="flex flex-center column" style="min-height:120px">
                  <q-spinner-dots size="3em" color="indigo-7" />
                  <div class="q-mt-sm text-grey">Загрузка...</div>
                </div>
              </td>
            </tr>
            <tr v-else-if="!filteredRows.length">
              <td colspan="11" class="text-center q-pa-lg text-grey">
                <q-icon name="inbox" size="2em" class="q-mb-sm" /><br>
                Нет данных для отображения
              </td>
            </tr>

            <template v-for="r in filteredRows" :key="r.row_key">
              <!-- Шапка group: work_guid -->
              <tr :class="['storno-group-row', r.source === 'only_usp' ? 'storno-row--only-usp' : 'disc-row-ok']" @click="r.source !== 'only_usp' && toggleExpand(r)">
                <td class="text-center disc-cell-expand">
                  <q-icon v-if="r.source !== 'only_usp'" :name="isExpanded(r) ? 'expand_less' : 'expand_more'" size="18px" color="indigo-7" />
                  <q-icon v-else name="block" size="16px" color="grey-4">
                    <q-tooltip>Только в УСП — сторнирование недоступно</q-tooltip>
                  </q-icon>
                </td>
                <td class="disc-cell-check" @click.stop>
                  <q-checkbox
                    v-if="r.source !== 'only_usp'"
                    :model-value="groupCheckState(r)"
                    @update:model-value="toggleGroup(r, $event)"
                    size="xs" dense color="indigo-7"
                    toggle-indeterminate
                  />
                </td>

                <!-- Объект -->
                <td class="disc-cell-obj">
                  <div class="disc-obj-row">
                    <q-icon v-if="r.source === 'only_usp'" name="arrow_forward" size="14px" color="purple-5" class="disc-obj-icon" />
                    <q-icon v-else name="task_alt" size="14px" color="indigo-4" class="disc-obj-icon" />
                    <span class="disc-obj-type">{{ r.project_type_name || '—' }}</span>
                  </div>
                  <div class="disc-obj-meta">
                    <span v-if="r.block_name">Бл.{{ r.block_name }}</span>
                    <span v-if="r.level_num"> / Эт.{{ r.level_num }}</span>
                  </div>
                </td>

                <!-- Работа -->
                <td class="disc-cell-work">
                  <div class="disc-work-pct-row">
                    <span class="disc-work-pct" :class="pctClass(r.fact_pct)">
                      {{ r.fact_pct != null ? r.fact_pct + '%' : '—' }}
                    </span>
                    <span class="disc-work-name" :title="r.work_set_name">
                      {{ workSetShortName(r) }}
                    </span>
                    <q-badge color="indigo-2" text-color="indigo-10" class="q-ml-xs">
                      {{ factItemsFor(r).length }}
                    </q-badge>
                  </div>
                  <div class="disc-work-group-name" v-if="parseGroupLabel(r.group_name)" :title="parseGroupLabel(r.group_name)">
                    {{ parseGroupLabel(r.group_name) }}
                  </div>
                  <!-- Отличительные признаки: floor_map_id, work_set_id, work_guid -->
                  <div class="disc-work-markers" @click.stop>
                    <span v-if="r.floor_map_id" class="disc-marker disc-marker--fm" :title="`floor_map_id: ${r.floor_map_id}`">
                      <q-icon name="layers" size="11px" class="q-mr-xs" />fm:{{ r.floor_map_id }}
                    </span>
                    <template v-for="ws in workSetMarkers(r)" :key="ws.work_set_id">
                      <span class="disc-marker disc-marker--ws" :title="`work_set_id: ${ws.work_set_id} — ${ws.work_set_name}`">
                        <q-icon name="bookmark" size="11px" class="q-mr-xs" />ws:{{ ws.work_set_id }}
                      </span>
                    </template>
                    <span v-if="r.work_guid" class="disc-marker disc-marker--guid" :title="r.work_guid">
                      <q-icon name="fingerprint" size="11px" class="q-mr-xs" />{{ (r.work_guid || '').substring(0, 8) }}…
                      <q-btn flat dense round size="xs" icon="content_copy" class="disc-guid-copy"
                        @click.stop="copyToClipboard(r.work_guid)">
                        <q-tooltip>Скопировать GUID</q-tooltip>
                      </q-btn>
                    </span>
                  </div>
                </td>

                <!-- Наша смета -->
                <td class="disc-cell-money disc-cell-smeta">
                  <div class="disc-money-total">{{ fmtDisc(r.our_smeta_total) }}</div>
                  <div class="disc-money-detail">
                    <span>СМР {{ fmtDisc(r.our_smeta_smr) }}</span>
                    <span>ТМЦ {{ fmtDisc(r.our_smeta_mat) }}</span>
                  </div>
                </td>

                <!-- Смета УСП -->
                <td class="disc-cell-money disc-cell-smeta-usp">
                  <div class="disc-money-total">{{ fmtDisc(r.usp_smeta_total) }}</div>
                  <div class="disc-money-detail">
                    <span>СМР {{ fmtDisc(r.usp_smeta_smr) }}</span>
                    <span>ТМЦ {{ fmtDisc(r.usp_smeta_mat) }}</span>
                  </div>
                </td>

                <!-- Наши данные -->
                <td class="disc-cell-money disc-cell-ours">
                  <div class="disc-money-total">{{ fmtDisc(r.our_total) }}</div>
                  <div class="disc-money-detail">
                    <span>СМР {{ fmtDisc(r.our_smr) }}</span>
                    <span>ТМЦ {{ fmtDisc(r.our_materials) }}</span>
                  </div>
                </td>

                <!-- Данные УСП -->
                <td class="disc-cell-money disc-cell-usp">
                  <div class="disc-money-total">{{ fmtDisc(r.usp_total) }}</div>
                  <div class="disc-money-detail">
                    <span>СМР {{ fmtDisc(r.usp_smr) }}</span>
                    <span>ТМЦ {{ fmtDisc(r.usp_materials) }}</span>
                  </div>
                </td>

                <!-- Разница -->
                <td class="disc-cell-money" :class="discDeltaClass(r.delta_total)">
                  <div class="disc-money-total">{{ fmtDiscDelta(r.delta_total) }}</div>
                  <div class="disc-money-detail">
                    <span>СМР {{ fmtDiscDelta(r.delta_smr) }}</span>
                    <span>ТМЦ {{ fmtDiscDelta(r.delta_materials) }}</span>
                  </div>
                </td>

                <!-- % -->
                <td class="text-center text-weight-medium" :class="discDeltaClass(r.delta_pct)">
                  {{ r.delta_pct != null ? (r.delta_pct > 0 ? '+' : '') + r.delta_pct + '%' : '—' }}
                </td>

                <!-- Отправлено -->
                <td class="text-center text-caption">{{ discFmtDate(r.last_sent_at) }}</td>
              </tr>

              <!-- Раскрытие: плоский список факт-записей -->
              <tr v-if="isExpanded(r)" class="storno-detail-row">
                <td colspan="11" class="storno-detail-cell">
                  <table class="storno-fact-tbl">
                    <thead>
                      <tr>
                        <th style="width:36px"></th>
                        <th style="width:50px">#</th>
                        <th style="width:13%">Объект</th>
                        <th>Работа</th>
                        <th style="width:10%">Период</th>
                        <th style="width:10%">СМР (₸)</th>
                        <th style="width:10%">ТМЦ (₸)</th>
                        <th style="width:10%">Итого (₸)</th>
                        <th style="width:10%">Отправлено</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!factItemsFor(r).length">
                        <td colspan="9" class="text-center text-grey q-pa-md">Нет факт-записей</td>
                      </tr>
                      <tr
                        v-for="(it, idx) in factItemsFor(r)"
                        :key="it.usp_fact_id"
                        :class="['storno-fact-row', selectedFactMap[it.usp_fact_id] ? 'storno-fact-row--selected' : '', !it.sent_at ? 'storno-fact-row--unsent' : '']"
                        @click="toggleFact(it)"
                      >
                        <td class="text-center" @click.stop="toggleFact(it)">
                          <q-checkbox
                            :model-value="!!selectedFactMap[it.usp_fact_id]"
                            @update:model-value="toggleFact(it)"
                            size="xs" dense color="indigo-7"
                          />
                        </td>
                        <td class="text-grey-6 text-center">{{ idx + 1 }}</td>
                        <td>
                          <div class="text-weight-medium">{{ it.project_type_name || '—' }}</div>
                          <div class="text-caption text-grey-7">
                            <span v-if="it.block_name">Бл.{{ it.block_name }}</span>
                            <span v-if="it.level_num"> / Эт.{{ it.level_num }}</span>
                            <span class="storno-fm">fm:{{ it.floor_map_id }}</span>
                          </div>
                        </td>
                        <td>
                          <div :title="it.work_set_name">{{ it.work_set_name || '—' }}</div>
                          <div class="text-caption text-grey-7" v-if="it.group_name">{{ it.group_name }}</div>
                        </td>
                        <td class="text-center">
                          <span class="storno-period">{{ fmtPeriod(it.period) }}</span>
                        </td>
                        <td class="text-right">{{ fmtDisc(it.fact_smr) }}</td>
                        <td class="text-right">{{ fmtDisc(it.fact_mat) }}</td>
                        <td class="text-right text-weight-bold storno-fact-total">{{ fmtDisc(it.fact_total) }}</td>
                        <td class="text-center text-caption">
                          <span v-if="it.sent_at">{{ discFmtDate(it.sent_at) }}</span>
                          <q-badge v-else color="orange-6" text-color="white" label="не отправлено" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="disc-footer">
        <div class="disc-footer-row">
          <div class="disc-footer-label">ИТОГО по {{ filteredRows.length }} группам:</div>
          <div class="disc-footer-group disc-footer-smeta">
            <span class="disc-footer-title">Наша смета</span>
            <span>Итого: <b>{{ fmtDisc(summary.osm_total) }}</b></span>
          </div>
          <div class="disc-footer-group disc-footer-smeta-usp">
            <span class="disc-footer-title">Смета УСП</span>
            <span>Итого: <b>{{ fmtDisc(summary.usm_total) }}</b></span>
          </div>
          <div class="disc-footer-group disc-footer-ours">
            <span class="disc-footer-title">Наши данные</span>
            <span>Итого: <b>{{ fmtDisc(summary.our_total) }}</b></span>
          </div>
          <div class="disc-footer-group disc-footer-usp">
            <span class="disc-footer-title">Данные УСП</span>
            <span>Итого: <b>{{ fmtDisc(summary.usp_total) }}</b></span>
          </div>
          <div class="disc-footer-group disc-footer-delta">
            <span class="disc-footer-title">Разница</span>
            <span :class="discDeltaClass(summary.d_total)" class="text-weight-bold"><b>{{ fmtDiscDelta(summary.d_total) }}</b></span>
          </div>
          <template v-if="selectedFactIds.length">
            <div class="disc-footer-group storno-footer-selected">
              <span class="disc-footer-title">Выбрано {{ selectedFactIds.length }} зап.</span>
              <span>Сумма: <b>{{ fmtDisc(selectedSummary.fact_total) }}</b></span>
              <span>Работ: <b>{{ selectedWorkGuids.length }}</b></span>
            </div>
          </template>
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';

export default {
  name: 'PpmStornoDialog',

  props: {
    modelValue: Boolean,
    data: Object,
    projectName: { type: String, default: '' },
    presetFilterType: { type: String, default: null },
  },

  emits: ['update:modelValue', 'refresh', 'storno-selected'],

  setup() {
    const { fmtDisc, fmtDiscDelta, discFmtDate, discDeltaClass } = useFormatters();
    return { fmtDisc, fmtDiscDelta, discFmtDate, discDeltaClass };
  },

  data() {
    return {
      selectedFactMap: {},
      expandedMap: {},
      search: '',
      filterType: null,
      filterBlock: null,
      filterFloor: null,
      _v: 'storno-by-id-v2',
    };
  },

  computed: {
    /**
     * Группировка факт-записей по work_guid (одна корзина = все факты этой работы).
     * Фильтрация по типу делается в factItemsFor() через filterType.
     */
    factItemsByGuid() {
      const map = {};
      const items = (this.data && this.data.factItems) || [];
      for (const it of items) {
        const k = it.work_guid;
        if (!map[k]) map[k] = [];
        map[k].push(it);
      }
      return map;
    },

    totalFactCount() {
      return ((this.data && this.data.factItems) || []).length;
    },

    filteredFactCount() {
      let n = 0;
      for (const r of this.filteredRows) {
        n += this.factItemsFor(r).length;
      }
      return n;
    },

    /**
     * Уникальные типы проектов на каждый work_guid (для фильтрации и отображения списка типов в строке).
     * Берём из project_breakdown (тип-разбивка по фактам/смете) — нет дублей строк.
     */
    typesByWorkGuid() {
      const map = {};
      const rows = (this.data && this.data.rows) || [];
      for (const r of rows) {
        const set = new Set();
        for (const pb of (r.project_breakdown || [])) {
          if (pb.project_type_name) set.add(pb.project_type_name);
        }
        if (!set.size && r.project_type_name) set.add(r.project_type_name);
        map[r.work_guid] = [...set];
      }
      return map;
    },

    groupedRows() {
      const rawRows = (this.data && this.data.rows) || [];
      const rows = rawRows.map(r => ({
        ...r,
        row_key:   r.work_guid,
        fact_smr:  r.our_smr       || 0,
        fact_mat:  r.our_materials || 0,
        fact_total: r.our_total    || 0,
        fact_volume: r.fact_volume || 0,
        fact_pct:  (r.group_name && /Процент:\s*[\d.]+/.test(r.group_name))
          ? this.parsePct(r.group_name) : null,
      }));
      rows.sort((a, b) => {
        const t = (a.project_type_name || '').localeCompare(b.project_type_name || '', 'ru');
        if (t) return t;
        const bl = (a.block_name || '').localeCompare(b.block_name || '', 'ru');
        if (bl) return bl;
        if ((a.level_num || 0) !== (b.level_num || 0)) return (a.level_num || 0) - (b.level_num || 0);
        return (a.work_set_name || '').localeCompare(b.work_set_name || '', 'ru');
      });
      return rows;
    },

    typeOptions() {
      const s = new Set();
      // Берём типы из всех breakdown-разбивок (т.к. строка может содержать несколько типов)
      for (const arr of Object.values(this.typesByWorkGuid)) {
        for (const n of arr) s.add(n);
      }
      // Доп.: типы из row-уровня (на случай рядов без breakdown)
      for (const r of this.groupedRows) {
        if (r.project_type_name) s.add(r.project_type_name);
      }
      return [...s].sort((a, b) => a.localeCompare(b, 'ru'));
    },

    blockOptions() {
      const s = new Set();
      for (const r of this.groupedRows) {
        if (r.block_name) s.add(r.block_name);
      }
      return [...s].sort();
    },

    floorOptions() {
      const s = new Set();
      for (const r of this.groupedRows) {
        if (r.level_num) s.add(r.level_num);
      }
      return [...s].sort((a, b) => a - b).map(v => ({ label: String(v), value: v }));
    },

    filteredRows() {
      let rows = this.groupedRows;
      if (this.filterType) {
        // Строка проходит фильтр, если ХОТЯ БЫ ОДИН её тип (из breakdown или row-уровня) совпадает.
        // Это позволяет видеть row с work_guid, у которого тип распределён по нескольким project_type.
        rows = rows.filter(r => {
          const types = this.typesByWorkGuid[r.work_guid] || [];
          if (types.includes(this.filterType)) return true;
          return r.project_type_name === this.filterType;
        });
      }
      if (this.filterBlock) rows = rows.filter(r => r.block_name === this.filterBlock);
      if (this.filterFloor != null) rows = rows.filter(r => r.level_num === this.filterFloor);
      if (this.search) {
        const q = this.search.toLowerCase();
        rows = rows.filter(r =>
          (r.work_set_name || '').toLowerCase().includes(q) ||
          (r.group_name || '').toLowerCase().includes(q) ||
          (r.work_guid || '').toLowerCase().includes(q)
        );
      }
      return rows;
    },

    selectedFactIds() {
      return Object.keys(this.selectedFactMap).filter(k => this.selectedFactMap[k]);
    },

    selectedWorkGuids() {
      const items = (this.data && this.data.factItems) || [];
      const set = new Set();
      for (const it of items) {
        if (this.selectedFactMap[it.usp_fact_id]) set.add(it.work_guid);
      }
      return [...set];
    },

    uniquePeriodsCount() {
      const items = (this.data && this.data.factItems) || [];
      const set = new Set();
      for (const it of items) {
        if (!this.selectedFactMap[it.usp_fact_id]) continue;
        const period = String(it.period || '').substring(0, 7);
        set.add(`${it.work_guid}|${period}`);
      }
      return set.size;
    },

    summary() {
      let osm_total = 0, usm_total = 0, our_total = 0, usp_total = 0, d_total = 0;
      for (const r of this.filteredRows) {
        osm_total += r.our_smeta_total || 0;
        usm_total += r.usp_smeta_total || 0;
        our_total += r.our_total       || 0;
        usp_total += r.usp_total       || 0;
        d_total   += r.delta_total     || 0;
      }
      return { osm_total, usm_total, our_total, usp_total, d_total };
    },

    selectedSummary() {
      const items = (this.data && this.data.factItems) || [];
      let fact_total = 0;
      for (const it of items) {
        if (this.selectedFactMap[it.usp_fact_id]) fact_total += it.fact_total || 0;
      }
      return { fact_total };
    },
  },

  watch: {
    'data.factItems'() {
      this.selectedFactMap = {};
      this.expandedMap = {};
    },
    modelValue(val) {
      if (val && this.presetFilterType !== undefined) {
        this.filterType = this.presetFilterType;
      }
    },
  },

  methods: {
    factItemsFor(r) {
      const all = this.factItemsByGuid[r.work_guid] || [];
      if (!this.filterType) return all;
      // Жёсткая фильтрация по типу проекта внутри развёрнутого списка фактов
      return all.filter(it => (it.project_type_name || '') === this.filterType);
    },

    fmtPeriod(val) {
      if (!val) return '—';
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split('-');
      if (!y || !m || !d) return s;
      return `${d}.${m}.${y}`;
    },

    workSetShortName(r) {
      const name = r.work_set_name || '';
      const cleaned = name.replace(/\s+floor_map_id:\s*\d+\s*$/i, '');
      const parts = cleaned.split(',').map(s => s.trim()).filter(Boolean);
      if (!parts.length) return '—';
      if (parts.length === 1) return parts[0];
      return `${parts[0]} (+${parts.length - 1})`;
    },

    /**
     * Возвращает список work_set'ов для маркеров.
     * Если у строки несколько work_set (из work_set_breakdown) — показываем все ID,
     * иначе только основной r.work_set_id.
     */
    workSetMarkers(r) {
      const bd = r.work_set_breakdown;
      if (Array.isArray(bd) && bd.length > 0) {
        return bd.map(b => ({
          work_set_id:   b.work_set_id,
          work_set_name: b.work_set_name,
        }));
      }
      if (r.work_set_id) {
        return [{ work_set_id: r.work_set_id, work_set_name: r.work_set_name || '' }];
      }
      return [];
    },

    parsePct(groupName) {
      if (!groupName) return null;
      const m = groupName.match(/Процент:\s*([\d.]+)/);
      return m ? parseFloat(m[1]) : null;
    },

    parseGroupLabel(groupName) {
      if (!groupName) return '';
      return groupName.replace(/Процент:\s*[\d.]+\s*/, '').trim();
    },

    pctClass(pct) {
      if (pct == null) return 'disc-pct--none';
      if (pct > 100) return 'disc-pct--over';
      if (pct >= 99.9) return 'disc-pct--done';
      if (pct > 0) return 'disc-pct--partial';
      return 'disc-pct--zero';
    },

    isExpanded(r) {
      return !!this.expandedMap[r.row_key];
    },

    toggleExpand(r) {
      const copy = { ...this.expandedMap };
      if (copy[r.row_key]) delete copy[r.row_key];
      else copy[r.row_key] = true;
      this.expandedMap = copy;
    },

    expandAll() {
      const copy = {};
      for (const r of this.filteredRows) copy[r.row_key] = true;
      this.expandedMap = copy;
    },

    collapseAll() {
      this.expandedMap = {};
    },

    toggleFact(it) {
      const copy = { ...this.selectedFactMap };
      if (copy[it.usp_fact_id]) delete copy[it.usp_fact_id];
      else copy[it.usp_fact_id] = true;
      this.selectedFactMap = copy;
    },

    /** Состояние группового чекбокса: true / false / null (indeterminate) */
    groupCheckState(r) {
      const items = this.factItemsFor(r);
      if (!items.length) return false;
      let on = 0;
      for (const it of items) if (this.selectedFactMap[it.usp_fact_id]) on++;
      if (on === 0) return false;
      if (on === items.length) return true;
      return null;
    },

    toggleGroup(r, val) {
      const items = this.factItemsFor(r);
      const copy = { ...this.selectedFactMap };
      const turnOn = val === true || val === null;
      for (const it of items) {
        if (turnOn) copy[it.usp_fact_id] = true;
        else delete copy[it.usp_fact_id];
      }
      this.selectedFactMap = copy;
    },

    executeStorno() {
      if (!this.selectedFactIds.length) return;
      const items = ((this.data && this.data.factItems) || [])
        .filter(it => this.selectedFactMap[it.usp_fact_id]);
      this.$emit('storno-selected', items);
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$q.notify({ message: 'Скопировано', color: 'positive', timeout: 1000, position: 'top' });
      }).catch(() => {
        this.$q.notify({ message: 'Не удалось скопировать', color: 'negative', timeout: 1500, position: 'top' });
      });
    },
  },
};
</script>

<style scoped>
.disc-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
}
.disc-main-bar {
  height: auto !important;
  min-height: 44px;
  padding: 4px 8px;
}
.disc-main-titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: 8px;
  flex: 1;
  line-height: 1.2;
}
.disc-main-project {
  font-size: 11px;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.disc-main-title {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.disc-filters { flex-shrink: 0; background: #fafafa; }
.disc-tabs-row {
  flex-shrink: 0;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
  min-height: 36px;
  padding: 4px 8px;
}
.disc-scroll { flex: 1; overflow: auto; min-height: 0; }
.disc-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border-spacing: 0;
}
.disc-table th,
.disc-table td {
  font-size: 12px;
  padding: 5px 8px;
  vertical-align: top;
  border: 1px solid #e0e0e0;
}
.disc-table thead tr:first-child th {
  position: sticky;
  top: 0;
  z-index: 3;
  white-space: nowrap;
  text-align: center;
}
.disc-table thead tr:nth-child(2) th {
  position: sticky;
  top: 28px;
  z-index: 3;
  white-space: nowrap;
  text-align: center;
}
.disc-th-check {
  width: 32px !important;
  min-width: 32px;
  max-width: 32px;
  padding: 0 4px !important;
  text-align: center;
}
.disc-cell-check {
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  padding: 0 4px !important;
  text-align: center;
  vertical-align: middle;
}
.disc-cell-expand {
  width: 24px;
  min-width: 24px;
  max-width: 24px;
  padding: 0 !important;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
}
.disc-th-info {
  background: #eceff1;
  color: #37474f;
  font-weight: 600;
  border-bottom: 2px solid #b0bec5;
}
.disc-th-operfact {
  background: #ede7f6;
  color: #311b92;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #b39ddb;
}
.disc-th-ours {
  background: #e3f2fd;
  color: #0d47a1;
  font-weight: 600;
  border-bottom: 2px solid #64b5f6;
}
.disc-th-usp {
  background: #f3e5f5;
  color: #4a148c;
  font-weight: 600;
  border-bottom: 2px solid #ba68c8;
}
.disc-th-smeta-group {
  background: #e0f7fa;
  color: #006064;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #b2ebf2;
}
.disc-th-smeta {
  background: #e0f7fa;
  color: #006064;
  font-weight: 600;
  border-bottom: 2px solid #4dd0e1;
}
.disc-th-smeta-usp {
  background: #e8f5e9;
  color: #1b5e20;
  font-weight: 600;
  border-bottom: 2px solid #81c784;
}
.disc-th-delta {
  background: #fff3e0;
  color: #e65100;
  font-weight: 600;
  border-bottom: 2px solid #ffb74d;
}
.disc-cell-obj { vertical-align: middle; }
.disc-obj-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}
.disc-obj-icon { flex-shrink: 0; margin-top: 1px; }
.disc-obj-type {
  font-size: 11px;
  font-weight: 500;
  color: #37474f;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.disc-obj-meta {
  font-size: 10px;
  color: #78909c;
  margin-top: 2px;
  padding-left: 18px;
}
.disc-cell-work { line-height: 1.3; vertical-align: middle; }
.disc-work-pct-row { display: flex; align-items: flex-start; gap: 6px; }
.disc-work-pct {
  flex-shrink: 0;
  display: inline-block;
  min-width: 38px;
  text-align: center;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
}
.disc-pct--done    { background: #c8e6c9; color: #1b5e20; }
.disc-pct--partial { background: #fff9c4; color: #f57f17; }
.disc-pct--zero    { background: #eceff1; color: #78909c; }
.disc-pct--over    { background: #ffcdd2; color: #b71c1c; }
.disc-pct--none    { background: #eceff1; color: #b0bec5; }
.disc-work-name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #263238;
  line-height: 1.3;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.disc-work-group-name {
  font-size: 10px;
  color: #78909c;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.25;
}
.disc-work-guid {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
  padding: 0 4px;
  font-family: 'Roboto Mono', monospace;
  font-size: 10px;
  color: #90a4ae;
  background: #f5f5f5;
  border-radius: 3px;
  cursor: default;
}
.disc-guid-copy { font-size: 10px !important; min-height: 14px !important; }
.disc-guid-copy :deep(.q-icon) { font-size: 12px; }

.disc-work-markers {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  align-items: center;
}
.disc-marker {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  font-family: 'Roboto Mono', monospace;
  font-size: 10px;
  border-radius: 3px;
  white-space: nowrap;
  line-height: 1.3;
  cursor: default;
}
.disc-marker--fm   { background: #ede7f6; color: #5e35b1; }
.disc-marker--ws   { background: #e3f2fd; color: #1565c0; }
.disc-marker--guid { background: #f5f5f5; color: #607d8b; }
.disc-cell-money { text-align: right; vertical-align: middle; }
.disc-money-total { font-size: 12px; font-weight: 600; white-space: nowrap; }
.disc-money-detail { display: flex; flex-direction: column; margin-top: 2px; }
.disc-money-detail span { font-size: 10px; color: #90a4ae; white-space: nowrap; }
.disc-cell-smeta     { background: #f0fbfc; }
.disc-cell-smeta-usp { background: #f1f8f2; }
.disc-cell-ours      { background: #f5f9ff; }
.disc-cell-usp       { background: #faf5fc; }

/* Шапка-группа */
.storno-group-row { cursor: pointer; }
.storno-group-row:hover td { filter: brightness(0.97); }
.storno-row--only-usp { cursor: default; background: #f3e5f5; opacity: 0.7; }
.storno-row--only-usp:hover td { filter: none; }

/* Раскрытая детализация */
.storno-detail-row > td.storno-detail-cell {
  padding: 0 !important;
  background: #fafbff;
  border-top: 2px solid #5c6bc0;
  border-bottom: 2px solid #5c6bc0;
}
.storno-fact-tbl {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}
.storno-fact-tbl th,
.storno-fact-tbl td {
  font-size: 11px;
  padding: 4px 8px;
  border: 1px solid #e8eaf6;
  vertical-align: middle;
}
.storno-fact-tbl thead th {
  background: #e8eaf6;
  color: #283593;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}
.storno-fact-row {
  cursor: pointer;
  transition: background 0.1s;
}
.storno-fact-row:hover td {
  background: #f3f0ff;
}
.storno-fact-row--selected td {
  background: #e8eaf6 !important;
}
.storno-fact-row--selected:hover td {
  background: #dde0f7 !important;
}
.storno-fact-row--unsent td {
  background: #fff8e1;
  box-shadow: inset 3px 0 0 0 #f59e0b;
}
.storno-fact-row--unsent:hover td {
  background: #fff3cd;
}
.storno-fact-total { color: #311b92; }
.storno-fm {
  margin-left: 6px;
  padding: 0 5px;
  font-family: 'Roboto Mono', monospace;
  font-size: 10px;
  color: #5e35b1;
  background: #ede7f6;
  border-radius: 3px;
}
.storno-period {
  display: inline-block;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #1b5e20;
  background: #c8e6c9;
  border-radius: 3px;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.disc-footer {
  flex-shrink: 0;
  border-top: 2px solid #3949ab;
  background: #fafafa;
  padding: 6px 12px;
}
.disc-footer-row {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  flex-wrap: wrap;
}
.disc-footer-label { font-weight: 700; font-size: 13px; color: #37474f; white-space: nowrap; }
.disc-footer-group { display: flex; gap: 12px; align-items: center; padding: 4px 10px; border-radius: 6px; }
.disc-footer-title { font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
.disc-footer-smeta     { background: #e0f7fa; color: #006064; }
.disc-footer-smeta-usp { background: #e8f5e9; color: #1b5e20; }
.disc-footer-ours      { background: #e3f2fd; color: #0d47a1; }
.disc-footer-usp       { background: #f3e5f5; color: #4a148c; }
.disc-footer-delta     { background: #fff3e0; color: #e65100; }
.storno-footer-selected { background: #e8eaf6; color: #283593; border: 1px solid #9fa8da; }
</style>
